import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import { useCan } from '../hooks';
import { ACCOUNT_LABEL, ENGAGEMENT_LABEL, fmtDate, ROLE_LABEL, STAGE_LABEL } from '../format';
import type { Person } from '../types';
import { Avatar, ErrorNote, Icon, Kv, Loading, Modal, Pill, Section, Tabs, toast } from '../components/ui';
import { DocTable } from './Documents';
import type { DocSummary } from '../types';

const STAGE_TONE: Record<string, string> = { candidate: 'muted', prestart: 'accent', probation: 'accent', active: 'ok', separating: 'warn', separated: 'muted' };

export function People() {
  const can = useCan();
  const nav = useNavigate();
  const [adding, setAdding] = useState(false);
  const q = useQuery({ queryKey: ['people'], queryFn: () => api.get<Person[]>('/api/people') });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorNote error={q.error} />;
  return (
    <div className="page">
      <div className="page-h">
        <div><p className="eyebrow">Ongoing suitability</p><h1>People</h1><p className="lede">{q.data!.length} people — candidates, staff and contractors.</p></div>
        {can('people:write') && <button className="btn primary" onClick={() => setAdding(true)}>+ Add person</button>}
      </div>
      <div className="table-wrap">
        <table className="tbl click">
          <thead><tr><th>Name</th><th>Position</th><th>Engagement</th><th>Stage</th><th>Microsoft account</th><th>Compliance</th></tr></thead>
          <tbody>
            {q.data!.map((p) => (
              <tr key={p.id} tabIndex={0} onClick={() => nav(`/people/${p.id}`)} onKeyDown={(e) => e.key === 'Enter' && nav(`/people/${p.id}`)}>
                <td><span className="who"><Avatar name={p.name} size={30} /><strong>{p.name}</strong></span></td>
                <td className="wrap">{p.positionTitle ?? (p.roleKey ? ROLE_LABEL[p.roleKey] : '—')}</td>
                <td>{ENGAGEMENT_LABEL[p.engagementType]}{p.fte ? ` · ${p.fte} FTE` : ''}</td>
                <td><Pill tone={STAGE_TONE[p.stage]}>{STAGE_LABEL[p.stage] ?? p.stage}</Pill></td>
                <td><Pill tone={ACCOUNT_LABEL[p.account.status]?.[1]}>{ACCOUNT_LABEL[p.account.status]?.[0] ?? p.account.status}</Pill></td>
                <td>{p.compliance ? <Pill tone={p.compliance.flag ? 'warn' : 'ok'}>{p.compliance.flag ? 'Flagged' : 'Clear'}</Pill> : <span className="dim small">Not linked yet</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {adding && <PersonModal onClose={() => setAdding(false)} />}
    </div>
  );
}

export function PersonModal({ onClose, person }: { onClose: () => void; person?: Person }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [f, setF] = useState({
    givenName: person?.givenName ?? '', familyName: person?.familyName ?? '', personalEmail: person?.personalEmail ?? '', positionTitle: person?.positionTitle ?? '',
    roleKey: person?.roleKey ?? 'trainer', engagementType: person?.engagementType ?? 'casual', fte: person?.fte?.toString() ?? '', stage: person?.stage ?? 'candidate',
  });
  const up = (p: Partial<typeof f>) => setF({ ...f, ...p });
  const m = useMutation({
    mutationFn: () => {
      const body = { ...f, personalEmail: f.personalEmail || null, positionTitle: f.positionTitle || null, fte: f.fte ? Number(f.fte) : null };
      return person ? api.send('PATCH', `/api/people/${person.id}`, body) : api.send<{ id: string }>('POST', '/api/people', body);
    },
    onSuccess: (r) => { void qc.invalidateQueries({ queryKey: ['people'] }); void qc.invalidateQueries({ queryKey: ['person'] }); toast(person ? 'Saved' : 'Person added'); onClose(); if (!person && r && typeof r === 'object' && 'id' in r) nav(`/people/${(r as { id: string }).id}`); },
  });
  return (
    <Modal title={person ? `Edit ${person.name}` : 'Add a person'} onClose={onClose}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); m.mutate(); }}>
        <div className="two">
          <label className="field" htmlFor="p-given"><span>Given name</span><input id="p-given" className="txt" required value={f.givenName} onChange={(e) => up({ givenName: e.target.value })} /></label>
          <label className="field" htmlFor="p-family"><span>Family name</span><input id="p-family" className="txt" required value={f.familyName} onChange={(e) => up({ familyName: e.target.value })} /></label>
        </div>
        <label className="field" htmlFor="p-email"><span>Personal email <span className="dim">(for the account activation pass and signing links)</span></span><input id="p-email" type="email" className="txt" value={f.personalEmail} onChange={(e) => up({ personalEmail: e.target.value })} /></label>
        <label className="field" htmlFor="p-title"><span>Position title</span><input id="p-title" className="txt" value={f.positionTitle} onChange={(e) => up({ positionTitle: e.target.value })} /></label>
        <div className="two">
          <label className="field" htmlFor="p-role"><span>Role (picks checklist lists)</span>
            <select id="p-role" value={f.roleKey} onChange={(e) => up({ roleKey: e.target.value })}>{Object.entries(ROLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          </label>
          <label className="field" htmlFor="p-eng"><span>Engagement</span>
            <select id="p-eng" value={f.engagementType} onChange={(e) => up({ engagementType: e.target.value })}>{Object.entries(ENGAGEMENT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          </label>
        </div>
        <div className="two">
          <label className="field" htmlFor="p-fte"><span>FTE</span><input id="p-fte" type="number" step="0.1" min="0" max="1" className="txt" value={f.fte} onChange={(e) => up({ fte: e.target.value })} /></label>
          <label className="field" htmlFor="p-stage"><span>Stage</span>
            <select id="p-stage" value={f.stage} onChange={(e) => up({ stage: e.target.value })}>{Object.entries(STAGE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          </label>
        </div>
        {m.error && <ErrorNote error={m.error} />}
        <div className="row gap"><button className="btn primary" disabled={m.isPending}>{person ? 'Save' : 'Add person'}</button><button type="button" className="btn" onClick={onClose}>Cancel</button></div>
      </form>
    </Modal>
  );
}

export function PersonPage() {
  const { id } = useParams();
  const can = useCan();
  const [tab, setTab] = useState<'overview' | 'documents'>('overview');
  const [editing, setEditing] = useState(false);
  const q = useQuery({ queryKey: ['person', id], queryFn: () => api.get<Person>(`/api/people/${id}`) });
  const docs = useQuery({ queryKey: ['documents', id], queryFn: () => api.get<DocSummary[]>(`/api/documents?personId=${id}`), enabled: tab === 'documents' });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorNote error={q.error} />;
  const p = q.data!;
  const acct = ACCOUNT_LABEL[p.account.status];
  return (
    <div className="page">
      <Link className="link back" to="/people"><Icon n="back" s={15} /> People</Link>
      <div className="profile-h">
        <Avatar name={p.name} size={56} />
        <div>
          <h1>{p.name}</h1>
          <p className="lede">{p.positionTitle ?? (p.roleKey ? ROLE_LABEL[p.roleKey] : '')}</p>
          <p className="small dim">{ENGAGEMENT_LABEL[p.engagementType]}{p.fte ? ` · ${p.fte} FTE` : ''} · {STAGE_LABEL[p.stage]}{p.startDate ? ` · Starts ${fmtDate(p.startDate)}` : ''}{p.endDate ? ` · Last day ${fmtDate(p.endDate)}` : ''}</p>
        </div>
        {can('people:write') && <button className="btn" style={{ marginLeft: 'auto' }} onClick={() => setEditing(true)}>Edit</button>}
      </div>
      <Tabs value={tab} onChange={setTab} tabs={[{ id: 'overview', label: 'Overview' }, { id: 'documents', label: 'Documents' }]} />
      {tab === 'overview' && (
        <div className="grid-2">
          <Section title="Engagement">
            <Kv items={[
              ['Role', p.roleKey ? ROLE_LABEL[p.roleKey] : '—'], ['Personal email', p.personalEmail ?? '—'],
              ['Offer accepted', fmtDate(p.offerAcceptedDate)], ['Start date', fmtDate(p.startDate)], ['Last day', fmtDate(p.endDate)],
            ]} />
          </Section>
          <div className="stack">
            <Section title="Microsoft account">
              <Kv items={[['Username', p.account.upn ?? '—'], ['Status', <Pill key="s" tone={acct?.[1]}>{acct?.[0] ?? p.account.status}</Pill>], ['Note', p.account.note ?? '—']]} />
            </Section>
            <Section title="Checklists">
              {p.runs?.length ? <ul className="plain-list">{p.runs.map((r) => <li key={r.id}><Link className="link" to={`/checklists/${r.id}`}>{r.kind === 'onboarding' ? 'Onboarding' : 'Offboarding'} — CL-{String(r.id).padStart(4, '0')}</Link><span className="small dim">{r.status}</span></li>)}</ul> : <p className="empty">No checklists yet.</p>}
              {can('checklists:write') && <Link className="link" to={`/checklists?start=${p.id}`}>+ Start a checklist</Link>}
            </Section>
            <Section title="Compliance (read only)">
              {p.compliance ? <p><Pill tone={p.compliance.flag ? 'warn' : 'ok'}>{p.compliance.flag ? 'Flagged' : 'Clear'}</Pill> <span className="small">{p.compliance.reason}</span></p> : <p className="small dim">Link to the compliance system and PowerPro not configured yet.</p>}
            </Section>
          </div>
        </div>
      )}
      {tab === 'documents' && (docs.isLoading ? <Loading /> : <DocTable docs={docs.data ?? []} />)}
      {editing && <PersonModal person={p} onClose={() => setEditing(false)} />}
    </div>
  );
}
